import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", order: null });
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", order: null });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", order: data.order });
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else setState({ status: "timeout", order: null });
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-5 text-center">
      <span className="pointer-events-none absolute select-none font-display text-[16rem] font-extrabold leading-none text-bone/[0.04]">444</span>
      {state.status === "checking" && (
        <>
          <p className="font-mono text-xs tracking-[0.35em] text-fade">CONFIRMING YOUR ORDER…</p>
          <div className="mt-6 h-px w-40 animate-pulse bg-bone/40" />
        </>
      )}
      {state.status === "paid" && (
        <>
          <p className="font-mono text-[10px] tracking-[0.35em] text-fade">ORDER CONFIRMED {state.order ? `— ${state.order.id}` : ""}</p>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-bone sm:text-6xl">
            You're exactly where
            <br />
            you need to be.
          </h1>
          {state.order && <p className="mt-6 font-mono text-sm tracking-widest text-bone/70">TOTAL {fmt(state.order.total)}</p>}
          <p className="mt-3 max-w-md text-sm font-light text-neutral-400">Your pieces are being prepared. An alignment card is in the box — watch for the numbers.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/shop" data-testid="success-continue-shopping" className="bg-bone px-8 py-4 font-display text-xs font-extrabold uppercase tracking-[0.25em] text-ink hover:opacity-90">
              CONTINUE SHOPPING
            </Link>
            <Link to="/account" data-testid="success-view-orders" className="border border-bone/40 px-8 py-4 font-mono text-xs tracking-[0.25em] text-bone hover:bg-bone hover:text-ink">
              VIEW ORDERS
            </Link>
          </div>
        </>
      )}
      {(state.status === "timeout" || state.status === "missing") && (
        <>
          <p className="font-mono text-[10px] tracking-[0.35em] text-fade">STATUS PENDING</p>
          <h1 className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight text-bone">Still aligning.</h1>
          <p className="mt-4 max-w-md text-sm font-light text-neutral-400">We couldn't confirm the payment yet. If you were charged, your order will appear in your account shortly.</p>
          <Link to="/account" data-testid="success-check-account" className="mt-10 border border-bone/40 px-8 py-4 font-mono text-xs tracking-[0.25em] text-bone hover:bg-bone hover:text-ink">
            CHECK MY ACCOUNT
          </Link>
        </>
      )}
    </div>
  );
}
